import { currentConfig } from "./appConfig";
import { getEnv } from "./env";
import {
  getUploadImageOriginalPath,
  ImageReference,
  withRef,
  withRefOrUndefined,
  WithRef,
} from "@um/contracts";
import { uploadImageFunction, storage } from "./firebase";
import {
  getDownloadURL,
  StorageError,
  StorageObserver,
  ref as storageRef,
  TaskEvent,
  uploadBytesResumable,
  UploadTask,
  UploadTaskSnapshot,
} from "firebase/storage";
import { newId } from "./firestore";
import {
  Unsubscribe,
  onSnapshot,
  DocumentReference,
  Query,
  DocumentSnapshot,
  QuerySnapshot,
} from "firebase/firestore";

export { getEnv } from "./env";

// abstractions from capacitor for shared UX
export function isNativePlatform() {
  return currentConfig().isNativePlatform ?? false;
}

export function getPlatform() {
  const config = currentConfig();
  return (getEnv("PLATFORM") ||
    (config.getPlatform && config.getPlatform()) ||
    "web") as "ios" | "android" | "web";
}

export async function uploadAccountImage(
  accountId: string,
  file: File,
  isGenerated: boolean = false,
  prompt?: string
): Promise<ImageReference> {
  const base64Data = await fileToBase64(file);
  const result = await uploadImageFunction({
    accountId,
    isGenerated,
    base64Data,
    prompt,
  });
  return result.data.image;
}

type UploadImageTask = Omit<UploadTask, "on"> & {
  on(
    event: "state_changed",
    nextOrObserver?: null | ((snapshot: UploadTaskSnapshot) => unknown),
    error?: ((a: StorageError | any) => unknown) | null,
    complete?: (image: ImageReference) => void
  ): Unsubscribe;
};

export function uploadAccountImageResumable(
  accountId: string,
  file: File
): UploadImageTask {
  const id = newId();
  const blob = storageRef(storage, getUploadImageOriginalPath(accountId, id));
  // Use uploadBytesResumable for progress tracking
  const uploadTask = uploadBytesResumable(blob, file);
  return {
    ...uploadTask,
    on: (event, nextOrObserver, error, complete) => {
      return uploadTask.on(event, nextOrObserver, error, async () => {
        try {
          const url = await getDownloadURL(blob);
          const response = await uploadImageFunction({
            accountId,
            url,
            isGenerated: false,
          });
          complete?.(response.data.image);
        } catch (err) {
          error?.(err);
        }
      });
    },
  };
}

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === "string") {
        const base64String = reader.result.replace(/^data:.+;base64,/, "");
        resolve(base64String);
      } else {
        reject(new Error("Failed to convert file to base64"));
      }
    };
    reader.onerror = (error) => reject(error);
  });

export interface SnapshotWithRetryOptions {
  /**
   * Hold back snapshots that are not backed by the server until the first
   * server-confirmed one has been delivered.
   *
   * Firestore's latency compensation echoes a local write straight back to
   * listeners. For a document the client has never fetched, that echo is a
   * partial view containing ONLY the fields just written — and it is marked
   * `metadata.fromCache`. A subscriber that treats the first snapshot as
   * "loaded" then decides on a document that is mostly missing. That is how
   * a web-onboarded user resuming in the app was routed Home: an experiment
   * stamp wrote one field to Settings/account, the echo arrived without
   * `onboardingProgress`, and the resume was skipped (see useAuthStore).
   *
   * Opt-in, because most listeners are happy to render a cached view early.
   * Once a server snapshot has arrived, later cache-sourced ones (offline,
   * pending writes) pass through as before.
   */
  waitForServer?: boolean;
}

// Subscribe to a Firestore document or query with automatic retry on error.
// Document subscriptions deliver `undefined` when the document does not
// exist (yet) — it will be delivered once created.
export function onSnapshotWithRetry<T = unknown>(
  reference: DocumentReference<T>,
  onNext: (doc: WithRef<T> | undefined) => void,
  onError?: (error: Error) => void,
  retryPeriod?: number,
  options?: SnapshotWithRetryOptions
): Unsubscribe;
export function onSnapshotWithRetry<T = unknown>(
  query: Query<T>,
  onNext: (docs: WithRef<T>[]) => void,
  onError?: (error: Error) => void,
  retryPeriod?: number,
  options?: SnapshotWithRetryOptions
): Unsubscribe;
export function onSnapshotWithRetry<T = unknown>(
  reference: DocumentReference<T> | Query<T>,
  onNext: (snapshot: WithRef<T> | WithRef<T>[] | undefined) => void,
  onError?: (error: Error) => void,
  retryPeriod: number = 500,
  options: SnapshotWithRetryOptions = {}
): Unsubscribe {
  let unsubscribe: Unsubscribe | undefined;
  let unsubscribed = false;
  let hasReceivedResult = false;
  let hasReceivedServerResult = false;

  // Get path for logging - handle both DocumentReference and Query
  const getPath = () => {
    if ("path" in reference) {
      return reference.path;
    }
    return "query";
  };

  function subscribe() {
    const path = getPath();
    console.log(`[onSnapshotWithRetry] Subscribing to ${path}`);
    unsubscribe = onSnapshot(
      reference as any,
      // A server read can prime the cache before this listener attaches.
      // When that cached data is current, server confirmation changes only
      // metadata. Without this opt-in, waitForServer can wait indefinitely.
      { includeMetadataChanges: options.waitForServer === true },
      (snapshot: DocumentSnapshot<T> | QuerySnapshot<T>) => {
        hasReceivedResult = true;
        if (!snapshot.metadata.fromCache) {
          hasReceivedServerResult = true;
        } else if (options.waitForServer && !hasReceivedServerResult) {
          // A local echo or an offline read, before the server has ever
          // answered. Not what this subscriber wants to decide on; the real
          // snapshot follows and is delivered normally.
          console.log(
            `[onSnapshotWithRetry] Holding cache-only snapshot for ${path} until the server answers`
          );
          return;
        }
        // Missing documents surface as `undefined` (withRef would fabricate
        // a truthy `{ ref }` object for them).
        const result =
          snapshot instanceof DocumentSnapshot
            ? withRefOrUndefined(snapshot)
            : snapshot.docs.map(withRef);

        console.log(`[onSnapshotWithRetry] Received snapshot for ${path}:`);
        onNext(result);
      },
      (error: Error) => {
        const path = getPath();
        console.error(
          `[onSnapshotWithRetry] Error in snapshot stream for ${path}`,
          error
        );

        // Call onError only if we haven't received any documents yet
        if (!hasReceivedResult && onError) {
          onError(error);
        }

        setTimeout(() => {
          console.log(
            `[onSnapshotWithRetry] Retrying snapshot subscription for ${path}`
          );
          if (!unsubscribed) {
            subscribe();
          }
        }, retryPeriod);
      }
    );
  }

  subscribe();

  return () => {
    unsubscribed = true;
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

/**
 * Combines multiple Unsubscribe callbacks into a single function.
 * Calling the returned function will invoke all provided unsubscribe callbacks.
 *
 * @param unsubs Array of unsubscribe functions (or possibly undefined/null)
 * @returns A function that calls all provided unsubscribes
 */
export function combineSubscriptions(
  ...unsubs: Array<(() => void) | undefined | null>
): () => void {
  return () => {
    for (const unsub of unsubs) {
      if (typeof unsub === "function") {
        unsub();
      }
    }
  };
}
